/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowUpRight, X } from 'lucide-react'

import { SeverityMix } from './Board'
import {
  derivedDimensions,
  dimensionLabels,
  formatCount,
  formatDelta,
  formatPercent,
  formatValue,
  slackPermalink,
} from './model'
import { useMeetingReports, useReportCohorts } from './useReportedMeetings'

import styles from './ReportedMeetingsPage.module.scss'

import {
  type ReportCohort,
  type ReportDimension,
  type ReportFilters,
  type ReportGroup,
  type ReportType,
  reportTypes,
  severities,
  type Severity,
} from '@/api/meetingReportsObservability'
import Button from '@/components/Button'
import { compactAmPm } from '@/utils/compactAmPm'

type Refinement = { dimension: ReportDimension; value: string } | null

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const trapTab = (event: KeyboardEvent, panel: HTMLElement) => {
  const targets = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null)
  if (targets.length === 0) {
    event.preventDefault()
    return
  }
  const first = targets[0]
  const last = targets[targets.length - 1]
  const active = document.activeElement
  if (!panel.contains(active)) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
  } else if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

const refine = (filters: ReportFilters, refinement: Refinement): ReportFilters => {
  if (!refinement) return filters
  const { dimension, value } = refinement
  if (dimension === 'severity')
    return severities.includes(value as Severity) ? { ...filters, severity: value as Severity } : filters
  if (dimension === 'report_type')
    return reportTypes.includes(value as ReportType) ? { ...filters, report_type: value as ReportType } : filters
  return { ...filters, [dimension]: value }
}

const Cohorts = ({
  cohorts,
  hidden,
  refinement,
  onRefine,
}: {
  cohorts: ReportCohort[]
  hidden: ReportDimension
  refinement: Refinement
  onRefine: (next: Refinement) => void
}) => (
  <div className={styles.cohorts}>
    {cohorts
      .filter((cohort) => cohort.dimension !== hidden && cohort.values.length > 0)
      .map((cohort) => {
        const total = cohort.values.reduce((sum, value) => sum + value.report_count, 0) + cohort.other_count
        return (
          <section key={cohort.dimension} className={styles.cohort} aria-label={dimensionLabels[cohort.dimension]}>
            <h3>
              {dimensionLabels[cohort.dimension]}
              {derivedDimensions.has(cohort.dimension) && <small> derived</small>}
            </h3>
            <ul>
              {cohort.values.map((value) => {
                const active = refinement?.dimension === cohort.dimension && refinement.value === value.value
                return (
                  <li key={value.value}>
                    <button
                      type="button"
                      aria-pressed={active}
                      className={active ? styles.activeCohort : undefined}
                      onClick={() =>
                        onRefine(
                          active
                            ? null
                            : {
                                dimension: cohort.dimension,
                                value: value.value,
                              }
                        )
                      }
                    >
                      <span className={styles.cohortLabel}>{formatValue(cohort.dimension, value.value)}</span>
                      <span className={styles.cohortBar} aria-hidden="true">
                        <span
                          style={{
                            width: formatPercent(value.report_count, total),
                          }}
                        />
                      </span>
                      <span className={styles.num}>
                        {formatPercent(value.report_count, total)}
                        {value.s0_count > 0 && <em className={styles.s0Tag}> {value.s0_count} S0</em>}
                      </span>
                    </button>
                  </li>
                )
              })}
              {cohort.other_count > 0 && (
                <li className={styles.otherRow}>
                  <span className={styles.cohortLabel}>other</span>
                  <span />
                  <span className={styles.num}>{formatPercent(cohort.other_count, total)}</span>
                </li>
              )}
            </ul>
          </section>
        )
      })}
  </div>
)

const Reports = ({ filters }: { filters: ReportFilters }) => {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const cursor = cursors[cursors.length - 1]
  const reports = useMeetingReports(filters, cursor, true)
  if (reports.isError) return <p role="alert">Could not load reports.</p>
  if (!reports.data) return <p role="status">Loading reports…</p>
  return (
    <div className={styles.reports}>
      <ul>
        {reports.data.items.map((report) => {
          const slack = slackPermalink(report)
          return (
            <li key={report.id} className={styles.report}>
              <div className={styles.reportHead}>
                <span className={`${styles.pill} ${styles[report.severity]}`}>{report.severity.toUpperCase()}</span>
                <strong>{report.signature}</strong>
                <time dateTime={report.created_at}>{compactAmPm(new Date(report.created_at).toLocaleString())}</time>
              </div>
              <p className={styles.reportMeta}>
                {[
                  report.meeting_platform,
                  report.output_device && `out: ${report.output_device}`,
                  report.mic_device && `mic: ${report.mic_device}`,
                  report.app_version && `v${report.app_version}`,
                  report.job_status,
                  report.known_issue,
                  report.reporter_email,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {report.feedback && <blockquote>{report.feedback}</blockquote>}
              {report.rationale && <p className={styles.rationale}>{report.rationale}</p>}
              <div className={styles.reportLinks}>
                {report.viewer_meeting_id && (
                  <a
                    href={`/app/attribution/${encodeURIComponent(report.viewer_meeting_id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open viewer <ArrowUpRight size={12} aria-hidden="true" />
                  </a>
                )}
                {slack && (
                  <a href={slack} target="_blank" rel="noopener noreferrer">
                    Slack thread <ArrowUpRight size={12} aria-hidden="true" />
                  </a>
                )}
                <code>{report.meeting_id.slice(0, 8)}</code>
              </div>
            </li>
          )
        })}
      </ul>
      {reports.data.items.length === 0 && <p>No reports in this selection.</p>}
      <div className={styles.pager}>
        <Button
          title="Newer"
          type="light"
          isDisabled={cursors.length <= 1}
          onClick={() => setCursors((previous) => previous.slice(0, -1))}
        />
        <Button
          title="Older"
          type="light"
          isDisabled={!reports.data.next_cursor}
          isLoading={reports.isFetching}
          onClick={() => {
            const next = reports.data.next_cursor
            if (next) setCursors((previous) => [...previous, next])
          }}
        />
      </div>
    </div>
  )
}

const Drawer = ({
  dimension,
  group,
  filters,
  onClose,
}: {
  dimension: ReportDimension
  group: ReportGroup
  filters: ReportFilters
  onClose: () => void
}) => {
  const [refinement, setRefinement] = useState<Refinement>(null)
  const panel = useRef<HTMLElement>(null)
  const cohorts = useReportCohorts(filters, true)
  useEffect(() => {
    panel.current?.focus()
  }, [])
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      else if (event.key === 'Tab' && panel.current) trapTab(event, panel.current)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  const refined = refine(filters, refinement)
  const title = formatValue(dimension, group.value)
  return createPortal(
    <>
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" />
      <aside
        ref={panel}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reported-meetings-drawer-title"
        tabIndex={-1}
      >
        <header className={styles.drawerHead}>
          <div>
            <p className={styles.eyebrow}>{dimensionLabels[dimension]}</p>
            <h2 id="reported-meetings-drawer-title">{title}</h2>
          </div>
          <Button title={<X size={16} />} ariaLabel="Close details" type="light" onClick={onClose} />
        </header>
        <dl className={styles.drawerStats}>
          <div>
            <dt>Reports</dt>
            <dd>{formatCount(group.summary.report_count)}</dd>
          </div>
          <div>
            <dt>Meetings</dt>
            <dd>{formatCount(group.summary.meeting_count)}</dd>
          </div>
          <div>
            <dt>Reporters</dt>
            <dd>{formatCount(group.summary.reporter_count)}</dd>
          </div>
          <div>
            <dt>Δ prior window</dt>
            <dd>{formatDelta(group.summary.report_count, group.previous_report_count)}</dd>
          </div>
        </dl>
        <div className={styles.drawerMix}>
          <SeverityMix counts={group.summary.severity} total={group.summary.report_count} />
          <span aria-hidden="true">
            {severities
              .filter((severity) => group.summary.severity[severity] > 0)
              .map((severity) => `${severity.toUpperCase()} ${group.summary.severity[severity]}`)
              .join(' · ')}
          </span>
        </div>
        <h3 className={styles.sectionTitle}>Cohorts</h3>
        {cohorts.isError ? (
          <p role="alert">Could not load cohorts.</p>
        ) : cohorts.data ? (
          <Cohorts cohorts={cohorts.data.cohorts} hidden={dimension} refinement={refinement} onRefine={setRefinement} />
        ) : (
          <p role="status">Loading cohorts…</p>
        )}
        <h3 className={styles.sectionTitle}>
          Reports
          {refinement && (
            <>
              {' '}
              <span className={styles.refinement}>
                {dimensionLabels[refinement.dimension]}: {formatValue(refinement.dimension, refinement.value)}
                <button type="button" onClick={() => setRefinement(null)} aria-label="Clear cohort filter">
                  <X size={12} />
                </button>
              </span>
            </>
          )}
        </h3>
        <Reports key={JSON.stringify(refined)} filters={refined} />
      </aside>
    </>,
    document.body
  )
}

export default Drawer
